import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import prairiLockupGrey from '../assets/prairi-lockup-grey.png';
import LegalModal from './LegalModal';

const SOCIAL_LINKS = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/prairi',
    path: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
  },
  {
    label: 'X',
    href: 'https://x.com/PRAIRI_com',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.828L1.254 2.25H8.08l4.251 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z',
  },
  {
    label: 'Instagram',
    href: null,
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/prairimatch',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
] as const;

/** Extra scroll, past one screen, before Back to Top is worth showing. */
const TALL_PAGE_EXTRA_PX = 200;

/** Logged-in app footer (Alice Investors UX). Shown at the end of authenticated pages. */
const LoggedInFooter: React.FC = () => {
  const location = useLocation();
  const footerRef = useRef<HTMLElement>(null);
  const [legalModal, setLegalModal] = useState<{
    isOpen: boolean;
    type: 'terms' | 'privacy';
  }>({ isOpen: false, type: 'terms' });
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const update = () => {
      const footer = footerRef.current;
      if (!footer) {
        setShowBackToTop(false);
        return;
      }
      const footerTop = footer.getBoundingClientRect().top + window.scrollY;
      setShowBackToTop(footerTop > window.innerHeight + TALL_PAGE_EXTRA_PX);
    };
    update();
    window.addEventListener('resize', update);
    const observer = new ResizeObserver(update);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', update);
      observer.disconnect();
    };
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer ref={footerRef} className="mt-auto w-full bg-[#1a1a1a] pt-6 text-white lg:pt-8">
      {showBackToTop ? (
        <div className="flex justify-center pb-6">
          <button
            type="button"
            onClick={scrollToTop}
            className="inline-flex items-center gap-2 rounded-full bg-[#c0392b] px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-black/30 transition hover:bg-[#a93226]"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              aria-hidden
            >
              <path d="M18 15l-6-6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Back to Top
          </button>
        </div>
      ) : null}

      <div className="mx-auto max-w-6xl px-6 pb-10 lg:px-10">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-md">
            <div className="flex items-center gap-3">
              <img src={prairiLockupGrey} alt="PRAIRI" className="h-10 w-auto" />
            </div>
            <p className="mt-3 text-sm italic text-gray-300">
              Matching the right Startups to the right Investors
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 sm:gap-12">
            <div>
              <h3 className="mb-3 text-sm font-bold text-white">Merchandise</h3>
              <a
                href="/#merchandise"
                className="block text-sm text-gray-300 transition hover:text-white"
              >
                for the Startup Founder
              </a>
            </div>

            <div>
              <h3 className="mb-3 text-sm font-bold text-white">Contact us</h3>
              <Link
                to="/contact"
                className="mb-2 block text-sm text-gray-300 transition hover:text-white"
              >
                Partner with us
              </Link>
              <Link
                to="/contact"
                className="block text-sm text-gray-300 transition hover:text-white"
              >
                Customer support
              </Link>
            </div>

            <div className="col-span-2 sm:col-span-1">
              <h3 className="mb-3 text-sm font-bold text-white">Connect</h3>
              <div className="flex items-center gap-3">
                {SOCIAL_LINKS.map(social => {
                  const icon = (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <path d={social.path} />
                    </svg>
                  );
                  const className =
                    'flex h-9 w-9 items-center justify-center rounded-full bg-white text-black transition hover:opacity-90';

                  if (!social.href) {
                    return (
                      <span key={social.label} aria-label={social.label} className={className}>
                        {icon}
                      </span>
                    );
                  }

                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className={className}
                    >
                      {icon}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-6 text-xs text-gray-400 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} PRAIRI. All Rights Reserved. PRAIRI and its unicorn logo
            are registered trademarks.
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <button
              type="button"
              onClick={() => setLegalModal({ isOpen: true, type: 'terms' })}
              className="hover:text-white"
            >
              Terms &amp; conditions
            </button>
            <button
              type="button"
              onClick={() => setLegalModal({ isOpen: true, type: 'privacy' })}
              className="hover:text-white"
            >
              Privacy policy
            </button>
            <Link to="/cookies" className="hover:text-white">
              Cookie policy
            </Link>
            <Link to="/cookies" className="hover:text-white">
              Your privacy choices
            </Link>
          </div>
        </div>
      </div>

      <LegalModal
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ ...legalModal, isOpen: false })}
        type={legalModal.type}
      />
    </footer>
  );
};

export default LoggedInFooter;
